// Cầu nối frontend ↔ backend /lesson-builder.
// Khi ĐÃ đăng nhập (có JWT) → gọi API thật; khi chưa/đứt mạng → fallback localStorage
// để vẫn dùng/test được offline. Giữ nguyên shape LessonDoc/CourseDoc.
import { API_CONFIG } from '../../../config/api.config'
import { lessonStorage } from '../lib/lessonStorage'
import { courseStorage } from '../lib/courseStorage'
import type { LessonDoc } from '../types/LessonDoc'
import type { CourseDoc } from '../types/CourseDoc'

const BASE = `${API_CONFIG.python.apiUrl}/lesson-builder`

function getToken(): string {
  return (
    localStorage.getItem('jwt_token') ||
    localStorage.getItem('auth_token') ||
    localStorage.getItem('token') ||
    ''
  )
}

export function isAuthed(): boolean {
  return !!getToken()
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...(init?.headers || {}),
    },
  })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(`API ${res.status}: ${detail}`)
  }
  return res.json()
}

// ---------------- Lessons ----------------
export const lessonApi = {
  async list(): Promise<LessonDoc[]> {
    if (!isAuthed()) return lessonStorage.list()
    try {
      return await req<LessonDoc[]>('/lessons')
    } catch {
      return lessonStorage.list()
    }
  },
  async sharedWithMe(): Promise<LessonDoc[]> {
    if (!isAuthed()) return []
    try {
      return await req<LessonDoc[]>('/lessons/shared')
    } catch {
      return []
    }
  },
  async get(id: string): Promise<LessonDoc | null> {
    if (!isAuthed()) return lessonStorage.get(id)
    try {
      return await req<LessonDoc>(`/lessons/${id}`)
    } catch {
      return lessonStorage.get(id)
    }
  },
  async getBySlug(slug: string): Promise<LessonDoc | null> {
    if (!isAuthed()) return lessonStorage.getBySlug(slug)
    try {
      return await req<LessonDoc>(`/lessons/by-slug/${encodeURIComponent(slug)}`)
    } catch {
      return lessonStorage.getBySlug(slug)
    }
  },
  async save(doc: LessonDoc): Promise<LessonDoc> {
    if (!isAuthed()) return lessonStorage.save(doc)
    try {
      return await req<LessonDoc>('/lessons', { method: 'POST', body: JSON.stringify(doc) })
    } catch {
      return lessonStorage.save(doc)
    }
  },
  async publish(id: string): Promise<LessonDoc | null> {
    if (!isAuthed()) return lessonStorage.publish(id)
    try {
      return await req<LessonDoc>(`/lessons/${id}/publish`, { method: 'POST' })
    } catch {
      return lessonStorage.publish(id)
    }
  },
  async remove(id: string): Promise<void> {
    if (!isAuthed()) {
      lessonStorage.remove(id)
      return
    }
    // Gọi backend xoá; KHÔNG nuốt lỗi (trước đây catch âm thầm → backend còn nguyên).
    await req(`/lessons/${id}`, { method: 'DELETE' })
    lessonStorage.remove(id)   // dọn luôn bản nháp local nếu có
  },
}

// ---------------- Courses ----------------
export const courseApi = {
  async list(): Promise<CourseDoc[]> {
    if (!isAuthed()) return courseStorage.list()
    try {
      return await req<CourseDoc[]>('/courses')
    } catch {
      return courseStorage.list()
    }
  },
  async sharedWithMe(): Promise<CourseDoc[]> {
    if (!isAuthed()) return []
    try {
      return await req<CourseDoc[]>('/courses/shared')
    } catch {
      return []
    }
  },
  async get(id: string): Promise<CourseDoc | null> {
    if (!isAuthed()) return courseStorage.get(id)
    try {
      return await req<CourseDoc>(`/courses/${id}`)
    } catch {
      return courseStorage.get(id)
    }
  },
  async getBySlug(slug: string): Promise<CourseDoc | null> {
    if (!isAuthed()) return courseStorage.getBySlug(slug)
    try {
      return await req<CourseDoc>(`/courses/by-slug/${encodeURIComponent(slug)}`)
    } catch {
      return courseStorage.getBySlug(slug)
    }
  },
  async save(doc: CourseDoc): Promise<CourseDoc> {
    if (!isAuthed()) return courseStorage.save(doc)
    // Khi ĐÃ đăng nhập: KHÔNG nuốt lỗi → ném ra để UI báo (tránh "tưởng đã lưu" mà server chưa lưu).
    return await req<CourseDoc>('/courses', { method: 'POST', body: JSON.stringify(doc) })
  },
  async publish(id: string): Promise<CourseDoc | null> {
    if (!isAuthed()) return courseStorage.publish(id)
    return await req<CourseDoc>(`/courses/${id}/publish`, { method: 'POST' })
  },
  async remove(id: string): Promise<void> {
    if (!isAuthed()) {
      courseStorage.remove(id)
      return
    }
    await req(`/courses/${id}`, { method: 'DELETE' })
    courseStorage.remove(id)
  },
  // ── Ghi danh & tiến độ (cần đăng nhập) ──
  // Trả {ok}; nếu khoá PRO mà thiếu quyền → throw Error có message để UI hiển thị.
  async enroll(courseId: string): Promise<void> {
    if (!isAuthed()) throw new Error('Vui lòng đăng nhập để ghi danh.')
    await req(`/courses/${courseId}/enroll`, { method: 'POST' })
  },
  async progress(courseId: string): Promise<CourseProgress | null> {
    if (!isAuthed()) return null
    try {
      return await req<CourseProgress>(`/courses/${courseId}/progress`)
    } catch {
      return null
    }
  },
  async myEnrollments(): Promise<CourseEnrollmentItem[]> {
    if (!isAuthed()) return []
    try {
      return await req<CourseEnrollmentItem[]>('/my/enrollments')
    } catch {
      return []
    }
  },
  // ── Catalog công khai ──
  async publicCourses(): Promise<PublicCourse[]> {
    try {
      return await req<PublicCourse[]>('/courses/public')
    } catch {
      return []
    }
  },
  // ── Thống kê cho giáo viên ──
  async teacherCoursesStats(): Promise<TeacherCourseStat[]> {
    try {
      return await req<TeacherCourseStat[]>('/teacher/courses-stats')
    } catch {
      return []
    }
  },
  async stats(courseId: string): Promise<CourseStats | null> {
    try {
      return await req<CourseStats>(`/courses/${courseId}/stats`)
    } catch {
      return null
    }
  },
  // ── Đánh giá sao ──
  async getRating(courseId: string): Promise<CourseRating> {
    try {
      return await req<CourseRating>(`/courses/${courseId}/rating`)
    } catch {
      return { avg: 0, count: 0, myStars: null }
    }
  },
  async rate(courseId: string, stars: number, comment?: string): Promise<CourseRating> {
    const r = await req<{ ok: boolean; avg: number; count: number; myStars: number }>(
      `/courses/${courseId}/rate`, { method: 'POST', body: JSON.stringify({ stars, comment }) }
    )
    return { avg: r.avg, count: r.count, myStars: r.myStars }
  },
}

export interface CourseRating { avg: number; count: number; myStars: number | null }

export interface CourseProgressLesson { id: string; slug: string; title: string; completed: boolean }
export interface CourseProgress {
  enrolled: boolean
  total: number
  completed: number
  percent: number
  nextSlug: string | null
  lessons: CourseProgressLesson[]
}
export interface CourseEnrollmentItem { course_id: string; slug: string; title: string; coverImage?: string; enrolledAt?: string }

export interface PublicCourse {
  id: string; slug: string; title: string; subtitle?: string; coverImage?: string
  category?: string; level?: string; access?: 'free' | 'pro'; instructorName?: string
  ratingAvg?: number; ratingCount?: number
  lessonCount: number; enrolledCount: number
}
export interface TeacherCourseStat { id: string; slug: string; title: string; status: string; lessonCount: number; enrolledCount: number }
export interface CourseStatsLesson { id: string; title: string; slug: string; started: number; completed: number; avgScore: number | null; aiAsked?: number; avgContent?: number | null }
export interface CourseStatsStudent { studentName?: string; studentEmail?: string; completed: number; total: number; percent: number; avgScore: number | null }
export interface CourseStats {
  course: { id: string; title: string; slug: string }
  overview: { enrolled: number; completedCourse: number; avgPercent: number; lessons: number }
  perLesson: CourseStatsLesson[]
  perStudent: CourseStatsStudent[]
}

// ---------------- Sharing ----------------
export interface SharePayload {
  resource_type: 'lesson' | 'course'
  resource_id: string
  scope: 'all' | 'teacher'
  target_email?: string
}

export const shareApi = {
  async share(payload: SharePayload): Promise<{ id: string; ok: boolean }> {
    return req('/share', { method: 'POST', body: JSON.stringify(payload) })
  },
}

// ---------------- Kết quả học tập ----------------
export interface ResultPayload {
  card_id?: string
  lesson_slug?: string
  score: number
  max_score: number
  total: number
  correct: number
  detail?: any
}

export const resultApi = {
  // Nộp kết quả (cần đăng nhập). Không đăng nhập → bỏ qua, vẫn chấm phía client.
  async submit(lessonId: string, payload: ResultPayload): Promise<void> {
    if (!isAuthed() || !lessonId) return
    try {
      await req(`/lessons/${lessonId}/submit`, { method: 'POST', body: JSON.stringify(payload) })
    } catch {
      /* không chặn trải nghiệm học nếu lưu lỗi */
    }
  },
  // Danh sách kết quả (chủ bài / admin)
  async list(lessonId: string): Promise<any[]> {
    try {
      return await req(`/lessons/${lessonId}/results`)
    } catch {
      return []
    }
  },
}

// ---------------- Word → Bài học AI (Smart Course Converter) ----------------
// Gửi .docx lên server: parser pmaker (MathType→LaTeX, tách ảnh) + AI bám sát tài liệu
// → trả ContentItem[] (gồm block quiz tương tác) để tạo thẻ "rich" render bằng ContentRenderer.
export async function wordToLessonItems(
  file: File
): Promise<{ items: any[]; title: string; image_count: number }> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${BASE}/word-to-lesson`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: fd,
  })
  if (!res.ok) {
    const t = await res.text().catch(() => '')
    throw new Error(`API ${res.status}: ${t}`)
  }
  return res.json()
}

// ---------------- Word → AI 2 bước: KHUNG → SINH CHI TIẾT ----------------
export interface OutlineTopic { heading: string; hint?: string }
export interface OutlineSection { title: string; kind: 'mo_dau' | 'chinh' | 'ket_thuc'; topics: OutlineTopic[] }
export interface LessonOutline { title: string; sections: OutlineSection[] }

// B1: tải .docx → AI đề xuất khung. Trả source_id để dùng cho bước generate.
export async function aiOutline(
  file: File
): Promise<{ success: boolean; source_id: string; title: string; image_count: number; outline: LessonOutline }> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${BASE}/ai/outline`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: fd,
  })
  if (!res.ok) {
    const t = await res.text().catch(() => '')
    throw new Error(`API ${res.status}: ${t}`)
  }
  return res.json()
}

// B2: gửi khung (đã GV chỉnh) → AI sinh chi tiết từng phần (items: ContentItem[]).
export async function aiGenerate(
  sourceId: string,
  outline: LessonOutline,
  ctx?: { lop?: number; chuong?: number; bai?: number }
): Promise<{ success: boolean; title: string; sections: { title: string; kind: string; items: any[] }[]; image_count: number }> {
  return req('/ai/generate', { method: 'POST', body: JSON.stringify({ source_id: sourceId, outline, ctx }) })
}

/**
 * Lồng tiếng 1 phần bài giảng (hiện dùng cho phần Khởi động): server "dịch văn nói + đọc công thức
 * thành lời Việt" rồi TTS → trả URL mp3 (đã lưu cùng bài). Cần đăng nhập GV PRO; có trừ điểm AI.
 */
export async function narrateSection(
  lessonId: string,
  sectionId: string,
  text: string,
  voice?: string
): Promise<{ success: boolean; url: string; script: string }> {
  return req('/voice/narrate', {
    method: 'POST',
    body: JSON.stringify({ lesson_id: lessonId, section_id: sectionId, text, voice }),
  })
}

/**
 * Tải lên / ghi âm audio cho 1 phần bài giảng — KHÔNG dùng AI, KHÔNG trừ điểm.
 * Lưu cùng thư mục audio/ như narrate, trả URL để gắn vào audioUrl của phần đó.
 */
export async function uploadSectionAudio(
  lessonId: string,
  sectionId: string,
  blob: Blob,
  filename = 'audio'
): Promise<{ success: boolean; url: string }> {
  const fd = new FormData()
  fd.append('file', blob, filename)
  fd.append('lesson_id', lessonId)
  fd.append('section_id', sectionId)
  const res = await fetch(`${BASE}/voice/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: fd,
  })
  if (!res.ok) {
    const t = await res.text().catch(() => '')
    throw new Error(`API ${res.status}: ${t}`)
  }
  return res.json()
}

/** Kho video YouTube theo bài (GV đặt file link tại bank/lesson-videos/L#/C#/B#). */
export async function fetchVideoCatalog(
  lop?: number, chuong?: number, bai?: number
): Promise<{ youtubeId: string; url: string; title: string }[]> {
  if (!lop || !chuong || !bai) return []
  try {
    const r = await req<{ videos: any[] }>(`/video-catalog?lop=${lop}&chuong=${chuong}&bai=${bai}`)
    return r?.videos || []
  } catch {
    return []
  }
}
